import { useCallback, useMemo, useState } from 'react';

import { FieldMapOptionsModal } from '@components/FieldMapOptionsModal';
import { MAP_LAYERS } from '@components/FieldMapOptionsModal/FieldMapOptionsModal';

export const useFieldMapOptionsModal = ({
  treatmentZones,
  hasTreatmentZone = false,
  showTrialArea = true,
  showSsurgo = true,
  protocolId,
  trialId,
  displayVisitTrialSelectWithSingleOption = false,
  adjustMapForDrawer = false,
  showHistoricalVisits = true
}: {
  treatmentZones: { label: string; color: string }[];
  hasTreatmentZone: boolean;
  showTrialArea?: boolean;
  showSsurgo?: boolean;
  protocolId: string;
  trialId: string;
  displayVisitTrialSelectWithSingleOption?: boolean;
  /** Only enable for full-screen maps (e.g. Field Map). Partial-height maps should leave this false. */
  adjustMapForDrawer?: boolean;
  /** Hide visits/notes controls for multi-field maps (e.g. Trial Fields Map). */
  showHistoricalVisits?: boolean;
}) => {
  const [selectedLayers, setSelectedLayers] = useState<string[]>([MAP_LAYERS.TRIAL_AREA, MAP_LAYERS.TREATMENT_ZONES]);
  const [open, setOpen] = useState<boolean>(false);
  const [drawerBottomInset, setDrawerBottomInset] = useState(0);

  const onClose = useCallback(() => {
    setOpen(false);
    if (adjustMapForDrawer) {
      setDrawerBottomInset(0);
    }
  }, [adjustMapForDrawer]);

  const displayModal = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const onDrawerMetricsChange = useCallback(
    (metrics: { open: boolean; height: number }) => {
      if (!adjustMapForDrawer) {
        return;
      }

      setDrawerBottomInset(metrics.open ? metrics.height : 0);
    },
    [adjustMapForDrawer]
  );

  const display = useMemo(() => {
    if (!open) return null;

    return (
      <FieldMapOptionsModal
        selectedLayers={selectedLayers}
        treatmentZones={treatmentZones}
        onClose={onClose}
        onDrawerMetricsChange={onDrawerMetricsChange}
        onMapLayersChange={mapLayers => setSelectedLayers(mapLayers)}
        hasTreatmentZone={hasTreatmentZone}
        showTrialArea={showTrialArea}
        showSsurgo={showSsurgo}
        protocolId={protocolId}
        trialId={trialId}
        displayVisitTrialSelectWithSingleOption={displayVisitTrialSelectWithSingleOption}
        showHistoricalVisits={showHistoricalVisits}
      />
    );
  }, [
    open,
    selectedLayers,
    treatmentZones,
    hasTreatmentZone,
    showSsurgo,
    showTrialArea,
    onClose,
    onDrawerMetricsChange,
    trialId,
    protocolId,
    displayVisitTrialSelectWithSingleOption,
    showHistoricalVisits
  ]);

  return {
    displayModal,
    selectedLayers,
    display,
    drawerBottomInset: adjustMapForDrawer ? drawerBottomInset : undefined
  };
};
